import { Lightning, Router } from '@lightningjs/sdk'

import { getColor, grey920, transparent } from '../../constants/colors'
import { BORDER_RADIUS } from '../../constants/general'
import { BASE_ROUTES } from '../../constants/routes'
import { SportData } from '../../types/flo-experience'
import { clearLockIcon, createSpeechTextForCards } from '../../utils/cardUtils'
import { deepCopyProperties } from '../../utils/misc'
import { captureClick, getCardPosition, SEGMENT_TRACK_EVENTS } from '../../utils/segmentAnalytics'
import { calculateRenderInfo } from '../../utils/text'
import TvCarouselContainer from '../TvCarouselContainer/TvCarouselContainer'
import { EventVariation, getSportsDataLayout, RoutineVariation, VersusVariation } from './sportData'
import { getCardTemplate } from './templates'
import TvBaseCard from './TvBaseCard'

type SportDataVariation = VersusVariation | RoutineVariation | EventVariation

export default class TvEventCard extends TvBaseCard {
  static _template() {
    return {
      ...super._template(),
      Container: {
        w: (w) => w,
        h: (h) => h,
        rect: true,
        color: getColor(grey920),
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: BORDER_RADIUS,
        },
      },
    }
  }

  get image() {
    return this.tag('Image')
  }

  get placeholder() {
    return this.tag('Placeholder')
  }

  get title() {
    return this.tag('Title')
  }

  get id() {
    return this.data?.action?.content_metadata?.stream_id ?? 0
  }

  get eventDataWrapper() {
    return this.tag('EventDataWrapper')
  }

  get sportDataWrapper() {
    return this.tag('SportDataWrapper') as SportDataVariation
  }

  get speechText(): string {
    let actiontext =
      this.data?.action?.url.includes('event-hub') && !this.data?.action?.content_metadata
        ? ' Select for more details . '
        : ' Select to play . '

    return createSpeechTextForCards(this.data, actiontext, this.cardIndex, this.totalCards)
  }

  _init() {
    this.attachCardTemplate()
  }

  _focus() {
    super._focus()
    this.updateSpotlight()
  }

  _unfocus() {
    super._unfocus()
    this.cancelSpotlight()
  }

  _active() {
    this.image.on('txLoaded', () => {
      this.placeholder.alpha = 0
      this.image.setSmooth('alpha', 1)
      this.container.patch({
        color: transparent,
      })
    })
  }

  _handleEnter() {
    void captureClick(
      SEGMENT_TRACK_EVENTS.ListItemClicked,
      this.data?.action?.analytics,
      getCardPosition(this),
    )

    if (this.data?.action?.url.includes('event-hub') && !this.data?.action?.content_metadata) {
      const url = this.data?.action?.url
      const encodedUrl = encodeURIComponent(url)
      Router.navigate(`${BASE_ROUTES.EVENT_HUB}/${encodedUrl}`)
    } else if (this.data?.action?.content_metadata) {
      let playlist = {}

      if (this.parent?.parent?.parent instanceof TvCarouselContainer) {
        deepCopyProperties((this.parent?.parent?.parent as TvCarouselContainer)?.data, playlist)
      }

      Router.navigate(`${BASE_ROUTES.PLAYER}/${(<any>this.data).id}`, {
        ...this.data.action,
        subtitle: this.data.action?.content_metadata?.stream_name,
        imgUrl: this.data?.preview_image?.url,
        playlist,
      })
    }
  }

  attachCardTemplate() {
    clearLockIcon(this.data)

    this.container.patch(getCardTemplate(this.style, this.cardWidth, this.data))

    const truncatedTitle = this.getTruncatedTitleText()
    this.title.patch({
      text: {
        text: truncatedTitle,
      },
    })
  }

  getTruncatedTitleText() {
    const textRenderInfo = calculateRenderInfo(this.title.text, this.data?.title?.text)
    return textRenderInfo.lines.join(' ')
  }

  updateSpotlight() {
    if (this.rowIndex === 0) {
      this.fireAncestors('$updateSpotlight', this.data)
    }
  }

  cancelSpotlight() {
    if (this.rowIndex === 0) {
      this.fireAncestors('$cancelSpotlightTimeout')
    }
  }

  updateSportsData(data: SportData) {
    if (this.eventDataWrapper.visible) {
      this.eventDataWrapper.visible = false
    }

    this.sportDataWrapper.visible = true
    const layout = getSportsDataLayout(data, this.cardWidth)
    this.sportDataWrapper.childList.clear()
    this.sportDataWrapper.childList.add(layout)
  }

  _handleClick() {
    this._handleEnter()
  }
}
